import React from 'react';
import { CheckboxCard, DropdownOption, DropdownSearch, DropdownAsync, ListItemLayout, ListLayout, Pager, SearchBar } from '@keplerco/core';
import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { SelectionEntity } from '../../../../components/panels/selection/selection.models';
import { SearchSkillsRequest } from '../../../../models/overmind/search-params';
import { useAppActions, useAppState } from '../../../../overmind';
import { MAX_PAGE_SIZE } from '../../../../library/consts/consts';
import { SortByOption } from '../../../../enums/sort-by-option';
import selectionPanelStyles from '../../../../components/panels/selection/selection.module.css';
import { PeerAssessmentSkillResponse } from '../../../../overmind/actions';

export function SkillSelectionList({ selectedEntities, setSelectedEntities }: { selectedEntities: SelectionEntity<PeerAssessmentSkillResponse>[]; setSelectedEntities: (selectedEntities: SelectionEntity<PeerAssessmentSkillResponse>[]) => void }): React.ReactElement {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [request, setRequest] = useState<SearchSkillsRequest>({
    search: undefined,
    sortAscending: true,
    page: 1,
    pageSize: 5,
    companySlug: companyVariables.slug!,
    searchGlobally: false,
    roleSlugs: [],
    skillCategorySlug: undefined,
  });
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);
  const [isSearchingRole, setIsSearchingRole] = useState<boolean>(false);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<DropdownOption[]>([]);
  const [skillCategoryOptions, setSkillCategoryOptions] = useState<DropdownOption[]>([]);
  const [skills, setSkills] = useState<PeerAssessmentSkillResponse[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);

  async function getSkills(request: SearchSkillsRequest) {
    const response = await actions.getCompanySkills(request);
    setSkills(
      response?.skills.map(skill => ({
        id: skill.companyEntityId!,
        slug: skill.slug ?? '',
        name: skill.name,
        skillCategories: skill.skillCategories ?? [],
      })) ?? []
    );
    setTotalPages(response?.totalPages ?? 1);
  }

  async function searchRoleOptions(search: string) {
    setIsSearchingRole(true);
    const response = await actions.getCompanyRoles({
      companySlug: companyVariables.slug!,
      search,
      sortAscending: true,
      page: 1,
      pageSize: 10,
      searchGlobally: false,
    });
    setRoleOptions(response?.roles.map(role => ({ label: role.name, value: role.slug! })) ?? []);
    setIsSearchingRole(false);
  }

  useEffect(() => {
    async function getSkillCategoryOptions() {
      const skillCategoryResponse = await actions.searchSkillCategories({ sortBy: SortByOption.AlphabeticalAtoZ, page: 1, pageSize: MAX_PAGE_SIZE });
      setSkillCategoryOptions(skillCategoryResponse?.results.map(skillCategory => ({ label: skillCategory.name, value: skillCategory.slug })) ?? []);
    }

    async function initialize() {
      setIsLoading(true);
      await getSkillCategoryOptions();
      await getSkills(request);
      setIsLoading(false);
    }

    initialize();
  }, []);

  function handleSkillSelection(skill: PeerAssessmentSkillResponse, isSelected: boolean) {
    if (isSelected) {
      setSelectedEntities([...selectedEntities, { slug: skill.slug, name: skill.name, entity: skill }]);
    } else {
      setSelectedEntities(selectedEntities.filter(selectedSkill => selectedSkill.slug !== skill.slug));
    }
  }

  return (
    <SkeletonOverlay isLoading={isLoading}>
      <div className={selectionPanelStyles.selectionList}>
        <SearchBar
          label="Search"
          value={request.search}
          loading={isSearching}
          onInput={async event => {
            const search = (event.target as HTMLInputElement).value;
            if (search === request.search) return;
            setIsSearching(true);
            const nextRequest = { ...request, search, page: 1 };
            setRequest(nextRequest);
            await getSkills(nextRequest);
            setIsSearching(false);
          }}
          responsive
        />

        <div className={selectionPanelStyles.selectionListFilters}>
          <DropdownAsync
            label="Roles"
            options={roleOptions}
            value={request.roleSlugs}
            responsive
            dialogContained
            onChange={async event => {
              const roleSlugs = !!event.target.value ? [event.target.value] : [];
              if (roleSlugs.join(',') === request.roleSlugs?.join(',')) return;
              setIsLoading(true);
              const nextRequest = { ...request, roleSlugs, page: 1 };
              setRequest(nextRequest);
              await getSkills(nextRequest);
              setIsLoading(false);
            }}
            selectedOptions={selectedRoleOptions}
            setSelectedOptions={setSelectedRoleOptions}
            isSearching={isSearchingRole}
            onSearch={searchRoleOptions}
          />

          <DropdownSearch
            label="Skill category"
            options={skillCategoryOptions}
            value={request.skillCategorySlug}
            responsive
            onChange={async event => {
              const skillCategorySlug = event.target.value;
              if (skillCategorySlug === request.skillCategorySlug) return;
              setIsLoading(true);
              const nextRequest = { ...request, skillCategorySlug, page: 1 };
              setRequest(nextRequest);
              await getSkills(nextRequest);
              setIsLoading(false);
            }}
          />
        </div>

        {!!skills.length ? (
          <React.Fragment>
            <ListLayout gap={12}>
              {skills.map(skill => {
                const isSelected = selectedEntities.some(selectedEntity => selectedEntity.slug === skill.slug);
                return (
                  <ListItemLayout key={skill.slug}>
                    <CheckboxCard id={skill.slug!} checked={isSelected} onChange={() => handleSkillSelection(skill, !isSelected)}>
                      <div className="card">
                        <h5>{skill.name}</h5>
                        <div style={{ color: 'var(--text_1)' }}>{skill.skillCategories?.map(skillCategory => skillCategory.name).join(', ')}</div>
                      </div>
                    </CheckboxCard>
                  </ListItemLayout>
                );
              })}
            </ListLayout>

            <div className={classNames({ invisible: totalPages <= 1 })}>
              <Pager
                activePageNumber={request.page ?? 1}
                pageCount={totalPages}
                onPageChange={async page => {
                  if (page === request.page) return;
                  setIsLoading(true);
                  const nextRequest = { ...request, page };
                  setRequest(nextRequest);
                  await getSkills(nextRequest);
                  setIsLoading(false);
                }}
              />
            </div>
          </React.Fragment>
        ) : (
          <EmptyState title="No skills found" />
        )}
      </div>
    </SkeletonOverlay>
  );
}
